'use client';

import Link from 'next/link';
import Layout from '../../components/Layout';
import SubDocsLayout from '../../components/SubDocsLayout';
import useTranslation from '../../lib/translation';
import SEOHead from '../../components/SEOHead';
import { useLocalePath } from '../../lib/useLocalePath';

const Tutorials = () => {
  const { t, lang } = useTranslation('common');
  const { prefix } = useLocalePath();
  const contactHref = prefix ? (lang === 'lt' ? `${prefix}/kontaktai` : `${prefix}/contact`) : (lang === 'lt' ? '/lt/kontaktai' : '/contact');

  return (
    <Layout>
      <SEOHead
        title={
          lang === 'lt' ? Array.isArray(t('tutorials.pageTitle')) ? (t('tutorials.pageTitle') as string[]).join(' ') : (t('tutorials.pageTitle') as string) : 'Tutorials | LB Websites'
        }
        description={
          lang === 'lt'
            ? Array.isArray(t('tutorials.description')) ? (t('tutorials.description') as string[]).join(' ') : (t('tutorials.description') as string)
            : 'Step-by-step tutorials for ordering websites and using the CMS from LB Websites.'
        }
        keywords='tutorials, guides, website management, CMS, content management, LB Websites tutorials'
        canonicalUrl={lang === 'lt' ? '/lt/sub/pamokos' : '/sub/tutorials'}
        lang={lang}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name:
            lang === 'lt'
              ? Array.isArray(t('tutorials.pageTitle')) ? (t('tutorials.pageTitle') as string[]).join(' ') : (t('tutorials.pageTitle') as string)
              : 'Tutorials | LB Websites',
          description:
            lang === 'lt'
              ? Array.isArray(t('tutorials.description')) ? (t('tutorials.description') as string[]).join(' ') : (t('tutorials.description') as string)
              : 'Step-by-step tutorials for ordering websites and using the CMS from LB Websites.',
          url: `https://www.lbvisible.com${
            lang === 'lt' ? '/lt/sub/pamokos' : '/sub/tutorials'
          }`,
          inLanguage: lang === 'lt' ? 'lt' : 'en',
        }}
      />

      <SubDocsLayout>
        <h1 className='text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-white mb-4 text-left'>
          {lang === 'lt' ? t('tutorials.heading') : 'Tutorials'}
        </h1>
        <p className='text-base text-gray-600 dark:text-gray-300 mb-10 text-left leading-relaxed'>
            {lang === 'lt'
              ? t('tutorials.subtitle')
              : "New to LB Websites? Here's how to get started with ordering your service and managing your site with ease."}
          </p>

          <div className='space-y-8'>
            <div>
              <h2 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>
                🛒{' '}
                {lang === 'lt'
                  ? 'Kaip užsakyti svetainę'
                  : 'How to Order a Website'}
              </h2>
              <ol className='list-decimal list-inside space-y-3'>
                <li className='text-sm text-gray-600 dark:text-gray-400 leading-relaxed'>
                  {lang === 'lt' ? 'Eikite į ' : 'Go to the '}
                  <span className='font-semibold text-gray-900 dark:text-white'>
                    {lang === 'lt' ? 'Kainos' : 'Pricing'}
                  </span>{' '}
                  {lang === 'lt'
                    ? 'puslapį ir pasirinkite planą, kuris geriausiai tinka jūsų verslui (Pradedantiesiems, Pažengusiems, Verslui).'
                    : 'page and choose the plan that best suits your business (Beginner, Advanced, Business).'}
                </li>
                <li>
                  {lang === 'lt' ? 'Spustelėkite ' : 'Click '}
                  <span className='font-semibold text-gray-700 dark:text-gray-100'>
                    {lang === 'lt' ? '"Užsakyti dabar"' : '"Order Now"'}
                  </span>{' '}
                  {lang === 'lt'
                    ? 'ir pereikite prie apmokėjimo. Mokėjimai saugiai apdorojami per Stripe.'
                    : 'and proceed to checkout. Payments are securely processed via Stripe.'}
                </li>
                <li>
                  {lang === 'lt'
                    ? 'Po pirkimo užpildykite projekto aprašymą su savo prekės ženklo informacija, turiniu ir nuostatomis.'
                    : 'After purchase, fill out the project brief with your brand info, content, and preferences.'}
                </li>
                <li>
                  {lang === 'lt'
                    ? 'Gausite patvirtinimą ir pradėsime kūrimą nedelsiant.'
                    : "You'll get a confirmation and we'll begin development immediately."}
                </li>
              </ol>
            </div>

            <div>
              <h2 className='text-lg font-semibold text-gray-900 dark:text-white mb-4'>
                ⚙️{' '}
                {lang === 'lt'
                  ? 'Kaip valdyti savo svetainę (TVS prieiga)'
                  : 'How to Manage Your Site (CMS Access)'}
              </h2>
              <ol className='list-decimal list-inside space-y-3'>
                <li>
                  {lang === 'lt'
                    ? 'Prisijunkite naudodami savo el. paštą ir slaptažodį iš svetainės klientų srities.'
                    : "Log in using your email and password from the website's client area."}
                </li>
                <li>
                  {lang === 'lt'
                    ? 'Eikite į TVS skydelį valdyti:'
                    : 'Navigate to the CMS dashboard to manage:'}
                  <ul className='list-disc list-inside ml-4 mt-2 space-y-1'>
                    <li>
                      {lang === 'lt'
                        ? 'Produktus ir kainas'
                        : 'Products and pricing'}
                    </li>
                    <li>
                      {lang === 'lt' ? 'Tinklaraščio įrašus' : 'Blog posts'}
                    </li>
                    <li>
                      {lang === 'lt'
                        ? 'Kategorijas ir banerus'
                        : 'Categories and banners'}
                    </li>
                    <li>
                      {lang === 'lt'
                        ? 'Užsakymus ir klientų informaciją'
                        : 'Orders & customer info'}
                    </li>
                  </ul>
                </li>
                <li>
                  {lang === 'lt'
                    ? 'Atnaujinkite bet kada. Pakeitimai išsaugomi realiu laiku ir iškart matomi jūsų gyvoje svetainėje.'
                    : 'Make updates anytime. Changes are saved in real-time and instantly visible on your live website.'}
                </li>
                <li>
                  {lang === 'lt'
                    ? 'Reikia pagalbos? Susisiekite su mumis per '
                    : 'Need help? Contact us via the '}
                  <Link
                    href={contactHref}
                    className='text-gray-700 dark:text-gray-200 font-medium underline hover:no-underline'
                  >
                    {lang === 'lt' ? t('footer.support') : 'Support'}
                  </Link>{' '}
                  {lang === 'lt' ? 'puslapį bet kada.' : 'page anytime.'}
                </li>
              </ol>
            </div>
          </div>
      </SubDocsLayout>
    </Layout>
  );
};

export default Tutorials;
